import { z } from 'zod';
import { AccountBindingIntent, AccountSyncFailureCode } from './account';
import { IngestPlatform } from './ingest';
import { PlanCode } from './plan';

export const PORTAL_EXTENSION_PROTOCOL_VERSION = 1 as const;

export const PortalExtensionAuthUser = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  display_name: z.string().min(1),
});
export type PortalExtensionAuthUser = z.infer<typeof PortalExtensionAuthUser>;

const portalRequestBase = z.object({
  protocol_version: z.literal(PORTAL_EXTENSION_PROTOCOL_VERSION),
  source: z.literal('ignite-portal'),
  target: z.literal('ignite-extension'),
  request_id: z.string().uuid(),
});

export const PortalExtensionStatusRequest = portalRequestBase.extend({
  type: z.literal('IGNITE_PORTAL_STATUS_REQUEST'),
});

export const PortalExtensionConnectRequest = portalRequestBase.extend({
  type: z.literal('IGNITE_PORTAL_CONNECT_REQUEST'),
  payload: z.object({
    code: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  }),
});

export const PortalExtensionDisconnectRequest = portalRequestBase.extend({
  type: z.literal('IGNITE_PORTAL_DISCONNECT_REQUEST'),
});

export const PortalExtensionAccountBindStartPayload = z
  .object({
    code: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    platform: IngestPlatform,
    intent: AccountBindingIntent,
    expires_at: z.string().datetime(),
    expected_external_id: z.string().min(1).max(128).nullable(),
  })
  .strict();
export type PortalExtensionAccountBindStartPayload = z.infer<
  typeof PortalExtensionAccountBindStartPayload
>;

export const PortalExtensionAccountBindStartRequest = portalRequestBase.extend({
  type: z.literal('IGNITE_PORTAL_ACCOUNT_BIND_START_REQUEST'),
  payload: PortalExtensionAccountBindStartPayload,
});

export const PortalExtensionAccountSyncStartRequest = portalRequestBase.extend({
  type: z.literal('IGNITE_PORTAL_ACCOUNT_SYNC_START_REQUEST'),
  payload: z
    .object({
      account_id: z.string().uuid(),
      external_id: z.string().min(1).max(128),
      binding_version: z.number().int().positive(),
      sync_run: z.object({
        id: z.string().uuid(),
        platform: IngestPlatform,
        account_id: z.string().uuid(),
        binding_version: z.number().int().positive(),
        status: z.enum(['pending', 'running', 'partial', 'completed', 'failed', 'cancelled']),
        selection_mode: z.enum(['window_90d', 'recent_50']).nullable(),
        selected_total: z.number().int().nonnegative(),
        succeeded_count: z.number().int().nonnegative(),
        partial_count: z.number().int().nonnegative(),
        failed_count: z.number().int().nonnegative(),
        failure_code: AccountSyncFailureCode.nullable(),
        batch_limit: z.number().int().positive().optional(),
        started_at: z.string().datetime().nullable(),
        completed_at: z.string().datetime().nullable(),
        created_at: z.string().datetime(),
        updated_at: z.string().datetime(),
      }),
    })
    .strict()
    .refine(
      (payload) =>
        payload.account_id === payload.sync_run.account_id &&
        payload.binding_version === payload.sync_run.binding_version,
      { message: 'account sync binding snapshot mismatch' },
    ),
});

export const PortalExtensionBridgeRequest = z.discriminatedUnion('type', [
  PortalExtensionStatusRequest,
  PortalExtensionConnectRequest,
  PortalExtensionDisconnectRequest,
  PortalExtensionAccountBindStartRequest,
  PortalExtensionAccountSyncStartRequest,
]);
export type PortalExtensionBridgeRequest = z.infer<typeof PortalExtensionBridgeRequest>;

export const PortalExtensionStatusPayload = z.object({
  installed: z.literal(true),
  extension_version: z.string().min(1),
  build_target: z.enum(['production', 'staging', 'development', 'unknown']),
  auth_state: z.enum(['connected', 'disconnected', 'expired']),
  auth_user: PortalExtensionAuthUser.nullable(),
  tenant_id: z.string().min(1).nullable(),
  token_expires_at: z.string().datetime().nullable(),
  pending_count: z.number().int().nonnegative(),
});
export type PortalExtensionStatusPayload = z.infer<typeof PortalExtensionStatusPayload>;

const portalResponseBase = z.object({
  protocol_version: z.literal(PORTAL_EXTENSION_PROTOCOL_VERSION),
  source: z.literal('ignite-extension'),
  target: z.literal('ignite-portal'),
  request_id: z.string().uuid(),
});

export const PortalExtensionStatusResponse = portalResponseBase.extend({
  type: z.literal('IGNITE_PORTAL_STATUS_RESPONSE'),
  ok: z.literal(true),
  payload: PortalExtensionStatusPayload,
});

export const PortalExtensionConnectResponse = portalResponseBase.extend({
  type: z.literal('IGNITE_PORTAL_CONNECT_RESPONSE'),
  ok: z.literal(true),
  payload: PortalExtensionStatusPayload,
});

export const PortalExtensionDisconnectResponse = portalResponseBase.extend({
  type: z.literal('IGNITE_PORTAL_DISCONNECT_RESPONSE'),
  ok: z.literal(true),
  payload: z.object({ disconnected: z.literal(true) }),
});

export const PortalExtensionAccountBindStartResponse = portalResponseBase.extend({
  type: z.literal('IGNITE_PORTAL_ACCOUNT_BIND_START_RESPONSE'),
  ok: z.literal(true),
  payload: z.object({
    accepted: z.literal(true),
    platform: IngestPlatform,
    intent: AccountBindingIntent,
    expires_at: z.string().datetime(),
    tab: z.enum(['opened', 'reused']),
  }),
});

export const PortalExtensionAccountSyncStartResponse = portalResponseBase.extend({
  type: z.literal('IGNITE_PORTAL_ACCOUNT_SYNC_START_RESPONSE'),
  ok: z.literal(true),
  payload: z.object({ accepted: z.literal(true) }),
});

export const PortalExtensionBridgeErrorCode = z.enum([
  'bridge.invalid_request',
  'bridge.protocol_unsupported',
  'bridge.background_unavailable',
  'bridge.extension_auth_required',
  'bridge.binding_platform_unsupported',
  'bridge.binding_pending_conflict',
  'bridge.creator_tab_failed',
  'auth.extension_connect_invalid_code',
  'auth.unauthorized',
  'internal_error',
]);
export type PortalExtensionBridgeErrorCode = z.infer<typeof PortalExtensionBridgeErrorCode>;

export const PortalExtensionErrorResponse = portalResponseBase.extend({
  type: z.literal('IGNITE_PORTAL_ERROR_RESPONSE'),
  ok: z.literal(false),
  error: z.object({
    code: PortalExtensionBridgeErrorCode,
    message: z.string().optional(),
  }),
});

export const PortalExtensionBridgeResponse = z.discriminatedUnion('type', [
  PortalExtensionStatusResponse,
  PortalExtensionConnectResponse,
  PortalExtensionDisconnectResponse,
  PortalExtensionAccountBindStartResponse,
  PortalExtensionAccountSyncStartResponse,
  PortalExtensionErrorResponse,
]);
export type PortalExtensionBridgeResponse = z.infer<typeof PortalExtensionBridgeResponse>;

export const ExtensionConnectCodeResponse = z.object({
  code: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  expires_at: z.string().datetime(),
});
export type ExtensionConnectCodeResponse = z.infer<typeof ExtensionConnectCodeResponse>;

export const ExtensionConnectExchangeRequest = z.object({
  code: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
});
export type ExtensionConnectExchangeRequest = z.infer<typeof ExtensionConnectExchangeRequest>;

export const ExtensionConnectExchangeResponse = z.object({
  token: z.string().min(1),
  user: PortalExtensionAuthUser,
  tenant_id: z.string().min(1),
  plan: z.object({
    code: PlanCode,
    display_name: z.string().trim().min(1).max(64),
  }),
  capture_quotas: z.object({
    page_capture: z.object({
      used: z.number().int().nonnegative(),
      limit: z.number().int().min(-1),
      remaining: z.number().int().nonnegative().nullable(),
      reset_at: z.string().datetime(),
    }),
    account_sync: z.object({
      used: z.number().int().nonnegative(),
      limit: z.number().int().min(-1),
      remaining: z.number().int().nonnegative().nullable(),
      reset_at: z.string().datetime(),
    }),
  }),
  expires_at: z.string().datetime(),
});
export type ExtensionConnectExchangeResponse = z.infer<typeof ExtensionConnectExchangeResponse>;

export const ExtensionAccountOverviewResponse = ExtensionConnectExchangeResponse.pick({
  user: true,
  tenant_id: true,
  plan: true,
  capture_quotas: true,
});
export type ExtensionAccountOverviewResponse = z.infer<typeof ExtensionAccountOverviewResponse>;

export const UserLogoutResponse = z.object({ ok: z.literal(true) });
export type UserLogoutResponse = z.infer<typeof UserLogoutResponse>;
